"use client";

import { useEffect, useRef, useState, type DragEvent, type ReactNode, type RefObject } from "react";
import {
  ArrowRight,
  Button,
  ChevronDown,
  CircleAlert,
  ClipboardPaste,
  FileSpreadsheet,
  Icon,
  Input,
  Textarea,
  Upload,
  cx,
} from "@/components";
import { FREE_ROW_CEILING } from "@/lib/ingest";

export type IntakeLoading = "file" | "paste" | "sheets" | null;
export type IntakeMethod = "paste" | "sheets";

export interface IntakeSurfaceProps {
  loading: IntakeLoading;
  error: string | null;
  /**
   * "standalone" (default) renders the full empty-state page: its own hero
   * header on the gradient wash. "hero" renders ONLY the working intake
   * stack (drop plate / paste / sheet link) so a host section — the landing
   * hero — provides the headline and surroundings. Same handlers either way.
   */
  variant?: "standalone" | "hero";
  /** Use the full-width, vertically stacked source cards from /maps/new. */
  presentation?: "default" | "equal-cards";
  /**
   * Deep-link focus (FAIL-458): the signed-in /maps/new launcher routes here as
   * `?intake=paste|sheet` so the matching method opens + focuses on mount. The
   * drop bar is always the primary path, so "upload" needs no hint.
   */
  initialMethod?: "paste" | "sheet";
  /**
   * First-class paste (FAIL-473): callers can pass the shared rich panel to
   * render inside the "Paste rows" method in place of the bare textarea.
   */
  pasteSlot?: ReactNode;
  /**
   * First-class Google Sheet (FAIL-474): callers can pass the shared rich
   * connect panel (or a Pro-lock card) in place of the bare URL box.
   */
  sheetSlot?: ReactNode;
  /** Optional shared ref so another CTA can open this exact file chooser. */
  fileInputRef?: RefObject<HTMLInputElement | null>;
  onMethodOpened?: (method: IntakeMethod) => void;
  onFilePickerOpened?: () => void;
  onFile: (file: File) => void;
  onPaste: (text: string) => void;
  onSheets: (url: string) => void;
}

const ACCEPT = ".csv,.tsv,.xlsx,.xls";
const PASTE_PLACEHOLDER = "Name\tAddress\tCity\nAcme Hardware\t414 Canal St\tNew Orleans";

/**
 * Intake — the zero-auth empty state (Take A). A confident horizontal drop
 * BAR is the primary path; paste rows and a public Google Sheet link are two
 * visually-distinct method cards whose inputs reveal on click, so neither reads
 * as an undesigned POC box.
 */
export function IntakeSurface({
  loading,
  error,
  variant = "standalone",
  presentation = "default",
  initialMethod,
  pasteSlot,
  sheetSlot,
  fileInputRef,
  onMethodOpened,
  onFilePickerOpened,
  onFile,
  onPaste,
  onSheets,
}: IntakeSurfaceProps) {
  const localFileInputRef = useRef<HTMLInputElement>(null);
  const activeFileInputRef = fileInputRef ?? localFileInputRef;
  const pasteRef = useRef<HTMLDetailsElement>(null);
  const sheetRef = useRef<HTMLDetailsElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [sheetUrl, setSheetUrl] = useState("");
  const busy = loading !== null;

  // FAIL-458: open + focus the method the launcher deep-linked to. Mount-only —
  // the drop bar stays the primary path, so an "upload" hint would be a no-op.
  useEffect(() => {
    if (!initialMethod) return;
    const details = initialMethod === "paste" ? pasteRef.current : sheetRef.current;
    if (details) details.open = true;
    const fieldId = initialMethod === "paste" ? "intake-paste" : "intake-sheet";
    document.getElementById(fieldId)?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  }

  /** The working controls — identical in both variants (one parse path).
   *  Take A (approved 2026-07-05): a confident horizontal drop BAR as the
   *  primary path, then paste + Google Sheet as two visually-distinct method
   *  cards (coral clipboard vs Sheets-green grid) whose inputs reveal on click,
   *  so neither reads as a bare POC box. */
  const controls = (
    <>
      <div
        className={cx("ingest-dropbar", dragging && "ingest-dropbar--active")}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <span className="ingest-dropbar__icon" aria-hidden>
          <Icon icon={Upload} size="xl" />
        </span>
        <span className="ingest-dropbar__text">
          <strong className="ingest-dropbar__title">Upload a spreadsheet.</strong>
          <span className="ingest-dropbar__hint">
            CSV, TSV, or Excel · up to {FREE_ROW_CEILING.toLocaleString()} rows free
          </span>
        </span>
        <Button
          variant="primary"
          loading={loading === "file"}
          iconLeading={<Icon icon={FileSpreadsheet} size="md" />}
          onClick={() => {
            onFilePickerOpened?.();
            activeFileInputRef.current?.click();
          }}
        >
          Choose a file
        </Button>
        <input
          ref={activeFileInputRef}
          className="ingest-drop__input"
          type="file"
          accept={ACCEPT}
          aria-label="Upload a spreadsheet"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = "";
          }}
        />
      </div>

      {error && (
        <div className="ingest-error" role="alert">
          <span className="ingest-error__icon">
            <Icon icon={CircleAlert} size="md" />
          </span>
          {error}
        </div>
      )}

      <div className="ingest-methods">
        <details ref={pasteRef} className="ingest-method ingest-method--paste">
          <summary
            className="ingest-method__face"
            onClick={() => {
              if (!pasteRef.current?.open) onMethodOpened?.("paste");
            }}
          >
            <span className="ingest-method__icon" aria-hidden>
              <Icon icon={ClipboardPaste} size="lg" />
            </span>
            <span className="ingest-method__body">
              <span className="ingest-method__title">Paste your map data.</span>
              <span className="ingest-method__hint">Copy and paste from Excel or Google Sheets</span>
            </span>
            <span className="ingest-method__chev" aria-hidden>
              <Icon icon={ChevronDown} size="md" />
            </span>
          </summary>
          <div className="ingest-method__panel">
            {pasteSlot ?? (
              <>
                <label className="ingest-field-label" htmlFor="intake-paste">
                  Your rows <em>keep the header row</em>
                </label>
                <Textarea
                  id="intake-paste"
                  className="ingest-method__url"
                  rows={4}
                  value={pasteText}
                  placeholder={PASTE_PLACEHOLDER}
                  aria-label="Paste spreadsheet rows"
                  onChange={(e) => setPasteText(e.target.value)}
                />
                <Button
                  variant="primary"
                  loading={loading === "paste"}
                  disabled={busy || pasteText.trim() === ""}
                  iconTrailing={<Icon icon={ArrowRight} size="sm" />}
                  onClick={() => onPaste(pasteText)}
                >
                  Map these rows
                </Button>
              </>
            )}
          </div>
        </details>

        <details ref={sheetRef} className="ingest-method ingest-method--sheet">
          <summary
            className="ingest-method__face"
            onClick={() => {
              if (!sheetRef.current?.open) onMethodOpened?.("sheets");
            }}
          >
            <span className="ingest-method__icon" aria-hidden>
              <Icon icon={FileSpreadsheet} size="lg" />
            </span>
            <span className="ingest-method__body">
              <span className="ingest-method__title">Import a Google Sheet.</span>
              <span className="ingest-method__hint">Your map will automatically update when your sheet does!</span>
            </span>
            <span className="ingest-method__chev" aria-hidden>
              <Icon icon={ChevronDown} size="md" />
            </span>
          </summary>
          <div className="ingest-method__panel">
            {sheetSlot ?? (
              <>
                <label className="ingest-field-label" htmlFor="intake-sheet">
                  Share link <em>set to “anyone with the link”</em>
                </label>
                <Input
                  id="intake-sheet"
                  className="ingest-method__url"
                  type="url"
                  value={sheetUrl}
                  placeholder="Paste the sheet’s share link"
                  aria-label="Public Google Sheets link"
                  onChange={(e) => setSheetUrl(e.target.value)}
                />
                <Button
                  variant="primary"
                  loading={loading === "sheets"}
                  disabled={busy || sheetUrl.trim() === ""}
                  iconTrailing={<Icon icon={ArrowRight} size="sm" />}
                  onClick={() => onSheets(sheetUrl.trim())}
                >
                  Fetch the sheet
                </Button>
              </>
            )}
          </div>
        </details>
      </div>
    </>
  );

  if (variant === "hero") {
    return (
      <div className={cx("ingest-intake--hero", presentation === "equal-cards" && "ingest-intake--equal-cards")}>
        {controls}
      </div>
    );
  }

  return (
    <div className="ingest-intake-wash">
      <div className="ingest-intake">
        <header className="ingest-intake__hero">
          <p className="ingest-intake__kicker">No sign-up needed</p>
          <h1 className="ingest-intake__title">Put your spreadsheet on the map.</h1>
          <p className="ingest-intake__sub">
            Drop a file of addresses or coordinates. The columns are read automatically — you
            confirm the match, and the map draws itself.
          </p>
        </header>
        {controls}
      </div>
    </div>
  );
}
